<template>
    <section class="p-3">
        <FormSection
            :full-height="false"
            title="Electronic Visit Verification (EVV) Log"
            :application-id="visitDetails.Application"
            :show-product-pill="true"
            :show-background="true"
            :use-gray-theme="true"
        >
            <template #content>
                <template v-if="isLoading">
                    <div
                        class="p-5 d-flex flex-column align-items-center justify-content-center text-center text-primary"
                    >
                        <i class="fa fa-spinner fa-spin font-size-5xl me-2 animated slow flash infinite"></i>
                        <strong class="mt-1">
                            <b>Loading...</b>
                        </strong>
                    </div>
                </template>
                <template v-else class="dialog position-relative">
                    <div class="dialog container ps-5 pt-3 row col-12" v-if="!isData">
                        <h5 class="fw-bold">Error</h5>
                        <p class="font-size-xl mb-1">
                            There was problem while loading the EVV Details. Please try again.
                        </p>
                        <span class="mb-2"> If the problem persists, report it. </span>
                        <button class="btn btn-primary w-20 ps-3" @click="getEvvDetails()">Try Again</button>
                    </div>
                    <div class="container row m-0 p-1">
                        <div class="dialog container row col-sm-12 col-md-6 mt-2">
                            <div class="col-4 ps-3 pe-0">
                                <div class="mt-2"><strong>Patient:</strong></div>
                                <hr />
                                <div><strong>MRN:</strong></div>
                                <hr />
                                <div><strong>Date of Birth:</strong></div>
                                <hr />
                                <div><strong>Patient Address:</strong></div>
                                <hr />
                                <div><strong>Visit Type:</strong></div>
                                <hr />
                                <div><strong>Payer:</strong></div>
                                <hr />
                                <div><strong>Assigned To:</strong></div>
                                <hr />
                                <div><strong>Visit Date:</strong></div>
                                <hr />
                                <div><strong>Verified Time In:</strong></div>
                                <hr />
                                <div><strong>Verified Time Out:</strong></div>
                                <hr />
                                <div v-if="evvDetails?.ClinicianVerifiedDateTime">
                                    <strong>Clinician Verified Date:</strong>
                                    <hr />
                                </div>
                            </div>
                            <div class="col-8 ps-0 pe-4">
                                <div class="mt-2 text-end">{{ evvDetails.PatientName }}</div>
                                <hr />
                                <div class="text-end">{{ evvDetails.PatientMRN }}</div>
                                <hr />
                                <div class="text-end">{{ formatDateFromDate(evvDetails.DateofBirth) }}</div>
                                <hr />
                                <div class="text-end">{{ evvDetails.PatientAddress }}</div>
                                <hr />
                                <div class="text-end">{{ evvDetails.TaskName }}</div>
                                <hr />
                                <div class="text-end">{{ evvDetails.AgencyPayerDisplayName }}</div>
                                <hr />
                                <div class="text-end">{{ evvDetails.UserName }}</div>
                                <hr />
                                <div class="text-end">{{ formatDateFromDate(evvDetails.VisitDate) }}</div>
                                <hr />
                                <div class="text-end">{{ formatHHEvvDate(evvDetails.VerifiedVisitTimeInDate) }}</div>
                                <hr />
                                <div class="text-end">{{ formatHHEvvDate(evvDetails.VerifiedVisitTimeOutDate) }}</div>
                                <hr />
                                <div class="text-end" v-if="evvDetails?.ClinicianVerifiedDateTime">
                                    {{ formatHHEvvDate(evvDetails.ClinicianVerifiedDateTime, true) }}
                                    <hr />
                                </div>
                            </div>
                        </div>
                        <div class="dialog container col-sm-12 col-md-6 mt-1">
                            <div class="form-group my-3 d-flex" v-if="evvDetails.PatientUnableToSignReason">
                                <label> Reason for Missing Patient Signature: </label>
                                <p class="ps-2 mb-0">{{ evvDetails.PatientUnableToSignReason }}</p>
                            </div>
                            <div class="form-group mb-3 row" v-if="patientSignatureAsset">
                                <label> Patient Signature </label>
                                <img
                                    class="img-fluid col-9"
                                    v-if="evvDetails.PatientSignatureType == 1"
                                    :src="patientSignatureAsset"
                                />
                                <audio
                                    v-if="evvDetails.PatientSignatureType == 2"
                                    class="col-7"
                                    controls
                                    :src="patientSignatureAsset"
                                ></audio>
                                <div v-if="evvDetails.PatientSignatureType == 2" class="col-12 pt-2">
                                    <button
                                        class="btn btn-primary w-25"
                                        @click="downloadAudio(patientSignatureAsset)"
                                        status="Download Audio"
                                    >
                                        Download Audio
                                    </button>
                                </div>
                            </div>
                            <div class="form-group mb-3 d-flex" v-if="evvDetails.ExceptionMessage.length">
                                <strong class="pe-2">Exception: </strong> {{ evvDetails.ExceptionMessage[0] }}
                                <div v-if="evvDetails.ExceptionMessage.length > 1">
                                    <div v-for="(value, index) in evvDetails.ExceptionMessage.slice(1)" :key="index">
                                        {{ value }}
                                    </div>
                                </div>
                            </div>
                            <div class="form-group mb-3 row" v-if="caretakerSignatureAsset">
                                <label> Caretaker Signature </label>
                                <img class="img-fluid col-9" :src="caretakerSignatureAsset" />
                            </div>
                            <div class="form-group mb-3 row" v-if="clinicianSignatureAsset">
                                <label> Clinician Signature </label>
                                <img class="img-fluid col-9" :src="clinicianSignatureAsset" />
                            </div>
                        </div>
                        <section class="flex-column col-12 px-3 py-2" v-if="evvDetails.VerifiedVisitTimeInDate">
                            <div class="row">
                                <img class="offset-lg-2 col-7" :src="evvDetails.MapsUrl" />
                                <ul class="list-unstyled col-2 ps-4">
                                    <li><i class="fa fa-map-marker-alt fa-2x text-danger p-2"></i> Patient Address</li>
                                    <li>
                                        <i class="fa fa-map-marker-alt fa-2x text-primary p-2"></i> Verified Start
                                        Location
                                    </li>
                                    <li>
                                        <i class="fa fa-map-marker-alt fa-2x text-success p-2"></i> Verified End
                                        Location
                                    </li>
                                </ul>
                            </div>
                        </section>
                    </div>
                    <div
                        class="d-flex justify-content-center mb-3 position-sticky bg-background-home-care-form-section p-2 z-index-10 bottom-0"
                        v-if="!isLoading"
                    >
                        <span>
                            <button type="button" class="ms-1 btn btn-md btn-outline-secondary" @click="closeDialog()">
                                Cancel
                            </button>
                        </span>
                    </div>
                </template>
            </template>
        </FormSection>
    </section>
</template>

<style scoped>
hr {
    margin: 0.5rem 0rem 0.5rem 0rem !important;
}
</style>

<script src="./EvvDetailsHomeHealth.ts"></script>
